import * as Sentry from '@sentry/vue';
import pick from 'lodash/pick';
import { redactSensitiveDataDeep } from '@nova/core';

/** @typedef {import('../../../core/src/user').IUser} IUser */

/**
 * Set user for Sentry context
 * @param {IUser|null} user
 */
export function setUser(user) {
  const data = !user?.id ? null : pick(user, 'id', 'email', 'role', 'orgId', 'companyId');
  Sentry.setUser(data);
}

/**
 * Forward to Sentry when the SDK is on the bundle (Helios init). Safe to call from non-Vue modules.
 * @param {unknown} err
 */
export function captureSentryException(err) {
  Sentry.captureException(err);
}

/**
 * Scrub password-reset JWTs and emails from every string leaf on a Sentry event/transaction.
 * Returns null (drop event) when redaction fails — fail closed rather than ship secrets.
 * @param {import('@sentry/vue').ErrorEvent | import('@sentry/vue').TransactionEvent} event
 */
function scrubSentryEvent(event) {
  return redactSensitiveDataDeep(event);
}

export default function initSentry(app, routes, config) {
  if (!config.sentry_dsn) {
    return;
  }

  Sentry.init({
    app,
    environment: config.server_env,
    dsn: config.sentry_dsn,
    release: config.nova_version,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Navigation cancelled from',
      'AxiosError',
      'NotAllowedError'
    ],
    // Filter errors from third-party scripts whose internals we cannot fix
    denyUrls: [
      // Beamer widget: internal race condition in hide/switch animation
      // causes "Cannot read properties of null (reading 'removeChild')"
      // See: https://loadsmart.sentry.io/issues/OPENDOCK-HELIOS-205
      /getbeamer\.com/u
    ],
    integrations: [
      // trackComponents installs a global mixin that wraps every component's
      // lifecycle hooks (bound per instance) and schedules a per-render timer.
      // On dense, frequently mounted/unmounted views (e.g. the yard board) this
      // retains stale component instances and their DOM subtrees, leaking memory.
      // Sentry defaults this to false; keep it off in production.
      Sentry.vueIntegration({ tracingOptions: { trackComponents: false } }),
      Sentry.browserTracingIntegration({ router: routes, routeLabel: 'path' })
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: config.sentry_traces_sample_rate ?? 0,
    beforeSend(event) {
      // Ignore Axios Errors for non internal server errors
      if (event.request?.statusCode >= 400 && event.request?.statusCode < 500) {
        return null;
      }

      return scrubSentryEvent(event);
    },
    beforeSendTransaction(event) {
      return scrubSentryEvent(event);
    }
  });
}
